import { Alert, Button, Link, Stack, Text, TextField } from '@solvhealth/jigsaw';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Trans, useTranslation } from 'react-i18next';
import { LEGAL_TOS_URL, PRIVACY_URL } from '~/constants';
import dataTestIds from '~/test/test-ids';
import Loading from '../../components/SolvPatternLibrary/Loading';
import {
  CODE_FIELD_NAME,
  PHONE_FIELD_NAME,
  PINK_DOOR_ACCEPTED_FIELD_NAME,
  TOS_ACCEPTED_FIELD_NAME,
} from '../../constants/phoneLoginForm';
import { analyticsTrackEvent, analyticsTrackFormErrors } from '../../core/analytics';
import {
  OTP_CONTINUE_WITHOUT_LOGIN,
  OTP_LOGIN_FIELD_FOCUS,
  OTP_LOGIN_SUBMIT_ERROR,
  OTP_RESEND_SMS,
  OTP_RESEND_VOICE_CALL,
  OTP_WRONG_NUMBER,
} from '../../core/analytics/events';
import { redirectAfterLogin, shouldFinishSignup } from '../../core/login';
import AuthenticationContext from '../../core/login/AuthenticationContext';
import { isPinkDoorExceptionUrl } from '../../core/util/account';
import { emptyFunction } from '../../core/util/function';
import { isEmptyObject } from '../../core/util/object';
import { tenDigitPhoneValueToFormattedDisplay } from '../../core/util/phoneNumber';
import { otpCodeInputValidator } from '../../core/util/rcForm/validators';
import { VerificationStatus } from '../PhoneLoginForm/util/verificationStatus';
import PersistedLoginInput from '../SignUpForm/Fields/PersistedLoginInput';

type Props = {
  form: formShape;
  handleOtpFormSubmit: (...args: any[]) => any;
  onLoginSuccess: (...args: any[]) => any;
  isBookingWidgetEmbedded?: boolean;
  redirectToUrl?: string;
  login?: any;
  preventRedirectAfterLogin?: boolean;
  accountSummary?: any;
  initialValues?: any;
  query?: any;
  requiresTosConsent?: boolean;
  goToSignUp: (...args: any[]) => any;
  handleFormSubmit: (...args: any[]) => any;
  verificationStatus: VerificationStatus;
  resetLoginData: (...args: any[]) => any;
  isBookingFlow?: boolean;
  externalSubmit?: boolean;
  resetExternalSubmit?: () => void;
  handleSkipLogin?: (phone: string) => void;
  redirectToRemix?: boolean;
};

const OtpCodeLoginForm = (props: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [, setCookie] = useCookies();
  const { isLoggedIn } = useContext(AuthenticationContext);
  const [isResubmitDisabled, setIsResubmitDisabled] = useState(false);
  const {
    form,
    handleOtpFormSubmit,
    handleFormSubmit,
    initialValues = {},
    requiresTosConsent,
    redirectToUrl,
    preventRedirectAfterLogin,
    isBookingWidgetEmbedded,
    goToSignUp,
    accountSummary,
    login,
    verificationStatus,
    resetLoginData,
    isBookingFlow,
    externalSubmit,
    resetExternalSubmit,
    handleSkipLogin,
    redirectToRemix,
  } = props;

  const { t } = useTranslation(['common']);

  const fieldError = form.getFieldError(CODE_FIELD_NAME);
  const isPinkDoorLogin = !isPinkDoorExceptionUrl();

  const onFocus = () => {
    analyticsTrackEvent(OTP_LOGIN_FIELD_FOCUS);
  };

  const onSubmitError = useCallback(
    (error: any) => {
      form.setFields({ [CODE_FIELD_NAME]: { value: '', errors: [error] } });
      setSubmitting(false);
    },
    [form]
  );

  const onSubmit = useCallback(
    (e: any) => {
      e && e.preventDefault();

      setSubmitting(true);
      form.validateFields((errors: any, values: any) => {
        if (isEmptyObject(errors)) {
          return handleOtpFormSubmit(values, { set: setCookie }, onSubmitError);
        }

        setSubmitting(false);
        analyticsTrackFormErrors(OTP_LOGIN_SUBMIT_ERROR, errors);
        return null;
      });
    },
    [form, handleOtpFormSubmit, onSubmitError, setCookie]
  );

  const handleResendCode = (contactMethod: any) => {
    if (!isResubmitDisabled) {
      handleFormSubmit(form.getFieldsValue(), contactMethod);
    }

    if (!isResubmitDisabled && contactMethod === 'voice') {
      analyticsTrackEvent(OTP_RESEND_VOICE_CALL);
    } else if (!isResubmitDisabled && contactMethod === 'sms') {
      analyticsTrackEvent(OTP_RESEND_SMS);
    }

    setIsResubmitDisabled(true);
  };

  useEffect(() => {
    if (externalSubmit) {
      resetExternalSubmit?.();
      onSubmit(null);
    }
  }, [externalSubmit, onSubmit, resetExternalSubmit]);

  useEffect(() => {
    if (isResubmitDisabled) {
      const timer = setTimeout(() => {
        setIsResubmitDisabled(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return emptyFunction;
  }, [isResubmitDisabled]);

  const handleResendSMSCode = () => handleResendCode('sms');

  const handleResendVoiceCode = () => handleResendCode('voice');

  const redirectAfterAccountReceived = () => {
    if (isBookingFlow) {
      setIsRedirecting(true);
      return;
    }

    if (shouldFinishSignup(accountSummary, verificationStatus)) {
      goToSignUp();
      return;
    }
    if (preventRedirectAfterLogin) return;
    if (isLoggedIn) {
      setIsRedirecting(true);

      redirectAfterLogin(
        accountSummary,
        redirectToUrl,
        isBookingWidgetEmbedded,
        false,
        redirectToRemix
      );
    }
  };

  if (!isRedirecting && !isEmptyObject(accountSummary)) {
    redirectAfterAccountReceived();

    return <Loading />;
  }

  if (isRedirecting) {
    return <Loading />;
  }

  const phoneValue = form.getFieldValue(PHONE_FIELD_NAME) || initialValues.phone;

  return (
    <Stack>
      <Alert
        icon={false}
        maxContentWidth="512px"
        show={fieldError && login.maxFailLimit}
        title={t('common:auth.errors.tooManyFailedAttempts', { count: 2 })}
        tone="critical"
        variant="banner"
      >
        {t('common:auth.errors.accountFrozen', { count: 2 })}
      </Alert>

      <Stack maxWidth="512px" space={4} {...(!isBookingFlow && { pt: 4, px: 2, mx: 'auto' })}>
        <Stack borderColor="gray-100" space={1}>
          <Text variant="h3">{t('common:auth.otpForm.title')}</Text>
          <Text muted>
            {t(
              phoneValue ? 'common:auth.otpForm.subtitleWithPhone' : 'common:auth.otpForm.subtitle',
              { phone: tenDigitPhoneValueToFormattedDisplay(phoneValue) }
            )}{' '}
            <Link
              inline
              onClick={(e: any) => {
                analyticsTrackEvent(OTP_WRONG_NUMBER);
                resetLoginData(e);
              }}
              type="button"
            >
              {t('common:auth.otpForm.wrongNumber')}
            </Link>
          </Text>
        </Stack>

        <Stack as="form" onSubmit={onSubmit} space={2}>
          <input
            {...form.getFieldProps(PHONE_FIELD_NAME, {
              initialValue: phoneValue,
            })}
            type="hidden"
          />

          <Stack space={3}>
            <TextField
              {...form.getFieldProps(CODE_FIELD_NAME, {
                rules: [
                  {
                    required: true,
                    validator: otpCodeInputValidator,
                    validateTrigger: ['onBlur'],
                  },
                ],
                initialValue: initialValues[CODE_FIELD_NAME] || '',
                onFocus,
              })}
              autoComplete="one-time-code"
              autoFocus
              data-testid={dataTestIds.login.otpEnter}
              error={
                fieldError &&
                !login.maxFailLimit &&
                login.accountError &&
                t('common:auth.otpForm.otpError')
              }
              fullWidth
              inputMode="numeric"
              maxLength="4"
              pattern="[0-9]*"
              style={{ height: 'auto' }}
              title={t('common:auth.otpForm.enterCode')}
            />
            <Text as="div" muted>
              {isBookingFlow ? (
                <Trans i18nKey="common:auth.otpForm.resendOrSkip" t={t}>
                  <Link inline onClick={handleResendSMSCode} type="button">
                    0
                  </Link>
                  <Link inline onClick={handleResendVoiceCode} type="button">
                    1
                  </Link>
                  <Link
                    inline
                    onClick={() => {
                      analyticsTrackEvent(OTP_CONTINUE_WITHOUT_LOGIN);
                      handleSkipLogin?.(phoneValue);
                    }}
                    type="button"
                  >
                    2
                  </Link>
                </Trans>
              ) : (
                <Trans i18nKey="common:auth.otpForm.resend" t={t}>
                  <Link inline onClick={handleResendSMSCode} type="button">
                    0
                  </Link>
                  <Link inline onClick={handleResendVoiceCode} type="button">
                    1
                  </Link>
                </Trans>
              )}
            </Text>
          </Stack>

          <PersistedLoginInput form={form} />

          <Stack space={2}>
            {requiresTosConsent && (
              <Text as="div" muted variant="body2">
                <Trans i18nKey="common:legal.agreeTosAndPp" t={t}>
                  <Link external href={LEGAL_TOS_URL} inline openInNewWindow size="small">
                    0
                  </Link>
                  <Link external href={PRIVACY_URL} inline openInNewWindow size="small">
                    1
                  </Link>
                </Trans>

                <input
                  {...form.getFieldProps(
                    isPinkDoorLogin ? PINK_DOOR_ACCEPTED_FIELD_NAME : TOS_ACCEPTED_FIELD_NAME,
                    {
                      initialValue: true,
                    }
                  )}
                  type="hidden"
                />
              </Text>
            )}

            {!isBookingFlow && (
              <Button
                data-testid={dataTestIds.login.otpSubmit}
                fullWidth
                loading={submitting}
                size="large"
                type="submit"
              >
                {t(
                  requiresTosConsent
                    ? 'common:auth.otpForm.continueAndAgree'
                    : 'common:auth.otpForm.verifyAndLogin'
                )}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default createForm()(OtpCodeLoginForm);
